/**
 * home_pref.ts — 로그인 직후 첫 화면 설정(내 계정·환경 › 첫 화면, 2026-11-02).
 *
 *   문서: users/{uid}/preferences/home = { kind, tenant?, biz?, updated_at }
 *     kind = "my"     내 업무(/my)                      — 설정 없을 때 기본(사용자 결정 10-26)
 *            "last"   마지막에 본 공간(SpaceContext defaultSpace = localStorage 마지막 공간)
 *            "tenant" 법인 사업 홈(/c/{tenant}/{biz})
 *   적용: "/" 의 SpaceIndexRedirect(router/TenantRoutes.tsx). 링크로 들어온 다른 주소는 그 주소가 우선.
 *   fallback: 소속이 빠졌거나 사업 권한이 없는 법인 홈 설정은 내 업무로(로그인 즉시 권한 없음 화면 방지).
 *   순수 모듈 — Firebase import 없음.
 */
import {
  BIZ_META,
  TENANT_BIZ,
  bizGate,
  bizHomePath,
  isBizKey,
  isBizReady,
  isTenantKey,
  spaceHomePath,
  tenantHasBiz,
  type BizKey,
  type SpaceKey,
  type TenantKey,
} from "./space";

export type HomePref =
  | { kind: "my" }
  | { kind: "last" }
  | { kind: "tenant"; tenant: TenantKey; biz: BizKey };

export const DEFAULT_HOME_PREF: HomePref = { kind: "my" };

/** 문서 → HomePref. 모르는 값·깨진 값은 기본(내 업무). 종류를 늘리면 여기 화이트리스트도 같이(AGENTS §8). */
export function parseHomePref(d: Record<string, unknown> | undefined): HomePref {
  if (!d) return DEFAULT_HOME_PREF;
  const kind = d.kind;
  if (kind === "my" || kind === "last") return { kind };
  if (kind === "tenant" && isTenantKey(d.tenant) && tenantHasBiz(d.tenant, d.biz)) {
    return { kind: "tenant", tenant: d.tenant, biz: d.biz };
  }
  return DEFAULT_HOME_PREF;
}

/** 라디오 value 로 쓰는 문자열 키 — "my" | "last" | "tenant:joongang:rmc" */
export function homePrefKey(p: HomePref): string {
  return p.kind === "tenant" ? `tenant:${p.tenant}:${p.biz}` : p.kind;
}
export function homePrefFromKey(key: string): HomePref | null {
  if (key === "my" || key === "last") return { kind: key };
  const [k, t, b] = key.split(":");
  if (k === "tenant" && isTenantKey(t) && isBizKey(b) && tenantHasBiz(t, b)) return { kind: "tenant", tenant: t, biz: b };
  return null;
}

export interface HomePrefContext {
  /** 열 수 있는 법인(소속) */
  tenants: readonly TenantKey[];
  /** 소속 문서의 사업(없으면 그 법인의 운영 중 사업 전부) */
  myBizs: Partial<Record<TenantKey, BizKey[]>>;
  /** 법인 기준 권한(석산 gate 등) */
  hasPermIn: (perm: string, tenant: TenantKey) => boolean;
  /** 마지막에 본 공간(열 수 있는 것으로 이미 보정된 값) */
  defaultSpace: SpaceKey;
}

/** 내가 첫 화면으로 고를 수 있는 법인 사업 홈 목록(소속 + 사업 준비됨 + 사업 권한) */
export function selectableTenantHomes(ctx: Pick<HomePrefContext, "tenants" | "myBizs" | "hasPermIn">): { tenant: TenantKey; biz: BizKey }[] {
  return ctx.tenants.flatMap((t) =>
    (ctx.myBizs[t]?.length ? ctx.myBizs[t]! : [...TENANT_BIZ[t]])
      .filter((b) => isBizReady(b))
      .filter((b) => {
        const gate = bizGate(b);
        return !gate || ctx.hasPermIn(gate, t);
      })
      .map((b) => ({ tenant: t, biz: b })),
  );
}

/** 설정 → 실제 이동 경로. 지금 열 수 없는 법인 홈이면 내 업무. */
export function resolveHomePath(pref: HomePref, ctx: HomePrefContext): string {
  if (pref.kind === "last") return spaceHomePath(ctx.defaultSpace);
  if (pref.kind === "tenant") {
    const ok = selectableTenantHomes(ctx).some((h) => h.tenant === pref.tenant && h.biz === pref.biz);
    return ok ? bizHomePath(pref.tenant, pref.biz) : spaceHomePath("my");
  }
  return spaceHomePath("my");
}

/** 사업 홈 라벨 — "레미콘 홈" */
export function bizHomeLabel(b: BizKey): string {
  return `${BIZ_META[b].label} 홈`;
}
